import {
  useMemo,
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { DataGrid, KeenIcon, Modal } from '@/components';
import usePagination from '@/hooks/usePagination';
import {
  useGetReportedPostsQuery,
  useModeratePostMutation,
  useGetPostByIdQuery,
  PostAction,
} from '@/services/PostService';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import {
  IReportedPostData,
  Post,
  PostModerationReasons,
} from '@/types/post-management.types';
import { ConfirmationDialog } from '@/components/dialogBox';
import { useSnackbar } from 'notistack';
import { PostModal } from './PostModal';
import { usePermissions } from '@/hooks/usePermissions';
import { ICommunityData } from '@/types/community-management.types';
import { CommunityRole } from '@/types/community-user.types';
import { useIntl } from 'react-intl';
import { formatDisplayDate, formatDisplayDateTime } from '@/utils/Date';

const renderTitleCell =
  (handleOpenPostModal: (postId: string) => void) =>
  (row: { row: { original: IReportedPostData } }) => {
    return (
      <button
        type="button"
        onClick={() => handleOpenPostModal(row.row.original.post_id)}
        className="text-sm font-medium text-blue-600 hover:underline focus:outline-none truncate max-w-[200px] inline-block text-left contrast:text-white"
        aria-label={`View details for ${row.row.original.title}`}
      >
        {row.row.original.title}
      </button>
    );
  };

const renderAuthorCell = ({
  row,
}: {
  row: { original: IReportedPostData };
}): JSX.Element => {
  const displayText =
    row.original.author?.name || row.original.author?.email || 'Unknown';
  const isTruncated = displayText.length > 20;
  const truncatedText = isTruncated
    ? `${displayText.slice(0, 20)}...`
    : displayText;
  return (
    <div className="relative inline-block group">
      <span
        className="text-gray-800 font-normal truncate max-w-[150px] inline-block"
        aria-label={isTruncated ? displayText : undefined}
      >
        {truncatedText}
      </span>
      {isTruncated && (
        <div className="absolute z-50 bottom-full left-0 mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-normal max-w-xs shadow-lg transition-opacity duration-200 invisible group-hover:visible">
          {displayText}
        </div>
      )}
    </div>
  );
};

const renderReportedByCell = ({
  row,
}: {
  row: { original: IReportedPostData };
}): JSX.Element => {
  const displayText: string =
    row.original.reported_by.name || row.original.reported_by.email || '';
  const isTruncated = displayText.length > 20;
  const truncatedText = isTruncated
    ? `${displayText.slice(0, 20)}...`
    : displayText;
  return (
    <div className="relative inline-block group">
      <span
        className="text-gray-800 font-normal truncate max-w-[150px] inline-block"
        aria-label={isTruncated ? displayText : undefined}
      >
        {truncatedText}
      </span>
      {isTruncated && (
        <div className="absolute z-50 bottom-full left-0 mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-normal max-w-xs shadow-lg transition-opacity duration-200 invisible group-hover:visible">
          {displayText}
        </div>
      )}
    </div>
  );
};

const renderReportedAtCell = (row: {
  row: { original: IReportedPostData };
}) => {
  return (
    <span className="text-gray-800 font-normal">
      {formatDisplayDate(new Date(row.row.original.reported_at))}
    </span>
  );
};

const renderReasonCell = ({
  row,
}: {
  row: { original: IReportedPostData };
}): JSX.Element => {
  const reason = row.original.reportReason || '';
  const isTruncated = reason.length > 50;
  const displayText = isTruncated ? `${reason.slice(0, 50)}...` : reason;

  return (
    <span className="text-sm text-gray-900 truncate max-w-[150px] inline-block">
      {displayText}
    </span>
  );
};

const renderStatusCell = (row: { row: { original: IReportedPostData } }) => (
  <span className="text-gray-800 font-normal">{row.row.original?.status}</span>
);

interface ReportDetailsModalProps {
  open: boolean;
  onClose: () => void;
  report: IReportedPostData | null;
}

const ReportDetailsModal = ({
  open,
  onClose,
  report,
}: ReportDetailsModalProps) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      className="!flex items-center justify-center"
      aria-labelledby="report-details-modal-title"
      aria-describedby="report-details-modal-description"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-gray-100 p-6 sm:p-8 w-full max-w-2xl mx-4 rounded-xl shadow-2xl flex flex-col max-h-[80vh]"
        role="document"
      >
        <h1
          id="report-details-modal-title"
          className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4 sm:mb-6"
        >
          Report Details
        </h1>
        <div id="report-details-modal-description" className="sr-only">
          View report details
        </div>
        <div
          className="flex-1 overflow-y-auto max-h-[70vh] pr-2 custom-scrollbar focus:outline-none focus:ring-2 focus:ring-blue-500"
          role="region"
          aria-label="Report details"
          tabIndex={0}
        >
          <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 md:gap-6">
            <div className="col-span-1">
              <span
                id="report-details-title-label"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Title
              </span>
              <div
                className="input input-bordered w-full h-11 rounded-lg border-gray-300 bg-gray-100 opacity-50 flex items-center px-3 contrast:text-white"
                aria-labelledby="report-details-title-label"
              >
                {report?.title || '-'}
              </div>
            </div>

            <div className="col-span-1">
              <span
                id="report-details-posted-by-label"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Posted By
              </span>
              <div
                className="input input-bordered w-full h-11 rounded-lg border-gray-300 bg-gray-100 opacity-50 flex items-center px-3 contrast:text-white"
                aria-labelledby="report-details-posted-by-label"
              >
                {report?.author?.name || '-'}
              </div>
            </div>

            <div className="col-span-1">
              <span
                id="report-details-reported-by-label"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Reported By
              </span>
              <div
                className="input input-bordered w-full h-11 rounded-lg border-gray-300 bg-gray-100 opacity-50 flex items-center px-3 contrast:text-white"
                aria-labelledby="report-details-reported-by-label"
              >
                {report?.reported_by?.name || '-'}
              </div>
            </div>

            <div className="col-span-1">
              <span
                id="report-details-reported-at-label"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Reported At
              </span>
              <div
                className="input input-bordered w-full h-11 rounded-lg border-gray-300 bg-gray-100 opacity-50 flex items-center px-3 contrast:text-white"
                aria-labelledby="report-details-reported-at-label"
              >
                {report?.reported_at
                  ? formatDisplayDateTime(new Date(report.reported_at))
                  : '-'}
              </div>
            </div>

            <div className="col-span-1">
              <span
                id="report-details-status-label"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Status
              </span>
              <div
                className="input input-bordered w-full h-11 rounded-lg border-gray-300 bg-gray-100 opacity-50 flex items-center px-3 contrast:text-white"
                aria-labelledby="report-details-status-label"
              >
                {report?.status || '-'}
              </div>
            </div>

            <div className="col-span-1 md:col-span-2">
              <span
                id="report-details-reason-label"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Reason
              </span>
              <div
                className="border border-gray-300 rounded-lg bg-light-active p-3 text-sm text-gray-900 max-h-60 overflow-y-auto whitespace-pre-wrap break-words"
                aria-labelledby="report-details-reason-label"
              >
                {report?.reportReason || '-'}
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 sm:mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-sm btn-ghost text-gray-600 hover:bg-gray-100 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-500"
            aria-label="Close report details"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

const ReportedPostListings = ({
  community,
}: {
  community: ICommunityData | null;
}) => {
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const communityId = useSelector(
    (state: RootState) => state.auth.selectedCommunityId
  );
  const intl = useIntl();
  const [confirmActionState, setConfirmActionState] = useState<{
    isOpen: boolean;
    postId: string | null;
    action: PostAction | null;
    reason?: string;
  }>({ isOpen: false, postId: null, action: null });
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    post: Post | null;
  }>({ isOpen: false, post: null });
  const [reportDetailsState, setReportDetailsState] = useState<{
    isOpen: boolean;
    report: IReportedPostData | null;
  }>({ isOpen: false, report: null });
  const [postIdToFetch, setPostIdToFetch] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  const [dropdownTriggerRect, setDropdownTriggerRect] =
    useState<DOMRect | null>(null);
  const [dropdownMenuPosition, setDropdownMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  useLayoutEffect(() => {
    if (!dropdownOpen || !dropdownTriggerRect) {
      setDropdownMenuPosition(null);
      return;
    }

    const menuEl = dropdownMenuRef.current;
    if (!menuEl) return;

    const menuRect = menuEl.getBoundingClientRect();
    const viewportPadding = 8;
    const verticalOffset = 8;

    const preferredLeft = dropdownTriggerRect.right - menuRect.width;
    const left = Math.min(
      Math.max(viewportPadding, preferredLeft),
      window.innerWidth - menuRect.width - viewportPadding
    );

    const spaceBelow = window.innerHeight - dropdownTriggerRect.bottom;
    const shouldOpenUp = spaceBelow < menuRect.height + verticalOffset;

    const top = shouldOpenUp
      ? Math.max(
          viewportPadding,
          dropdownTriggerRect.top - menuRect.height - verticalOffset
        )
      : Math.min(
          dropdownTriggerRect.bottom + verticalOffset,
          window.innerHeight - menuRect.height - viewportPadding
        );

    setDropdownMenuPosition({ top, left });
  }, [dropdownOpen, dropdownTriggerRect]);

  const queryParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue,
    community_id: communityId || undefined,
  };

  const { data, isLoading, isFetching, isError, refetch } =
    useGetReportedPostsQuery(queryParams, {
      skip: !communityId,
    });

  const {
    data: postData,
    isSuccess: postFetchSuccess,
    error: postFetchError,
  } = useGetPostByIdQuery(
    { communityId: communityId || '', postId: postIdToFetch! },
    {
      skip: !communityId || !postIdToFetch,
      refetchOnMountOrArgChange: true,
    }
  );

  const [moderatePost] = useModeratePostMutation();

  useEffect(() => {
    if (data) {
      handleTotalPages(data.total_pages || 1);
      handleTotalItems(data.total_items || 0);
    }
  }, [data, handleTotalPages, handleTotalItems]);

  useEffect(() => {
    if (postFetchSuccess && postData && postIdToFetch === postData.id) {
      setModalState((prev) => {
        if (prev.post?.id === postData.id && prev.post === postData)
          return prev;
        return { isOpen: true, post: postData };
      });
    }
  }, [postFetchSuccess, postData, postIdToFetch]);

  useEffect(() => {
    if (postFetchError) {
      enqueueSnackbar('Failed to fetch post details', { variant: 'error' });
      setPostIdToFetch(null);
    }
  }, [postFetchError, enqueueSnackbar]);

  useEffect(() => {
    if (typeof refetch === 'function') {
      refetch();
    }
  }, [debouncedValue, communityId, refetch]);

  const handleModeratePost = async (reasonPayload?: {
    reason: string;
    reasonDetails: string | null;
  }) => {
    if (!confirmActionState.postId || !confirmActionState.action) return;

    try {
      await moderatePost({
        postId: confirmActionState.postId,
        action: confirmActionState.action,
        reason: reasonPayload?.reason,
        reasonDetails: reasonPayload?.reasonDetails,
      }).unwrap();

      enqueueSnackbar(
        `Post ${confirmActionState.action.toLowerCase()}ed successfully`,
        { variant: 'success' }
      );

      // Close the confirmation dialog
      setConfirmActionState({
        isOpen: false,
        postId: null,
        action: null,
      });

      refetch();
    } catch (error) {
      const errorMessage =
        error && typeof error === 'object' && 'data' in error
          ? (error as { data?: { message?: string } })?.data?.message
          : error instanceof Error
            ? error.message
            : 'An error occurred';

      enqueueSnackbar(
        `Failed to ${confirmActionState.action?.toLowerCase()} post: ${errorMessage}`,
        { variant: 'error' }
      );
    }
  };

  const handleOpenPostModal = useCallback((postId: string) => {
    setModalState({ isOpen: true, post: null });
    setPostIdToFetch(postId);
  }, []);

  const filteredData = useMemo(() => {
    if (!data?.data) return [];
    return data.data.map((report) => ({
      id: report.id,
      post_id: report.post_id,
      title: report.post_title,
      content: report.post_content,
      author: {
        id: report.posted_by.id,
        name: report.posted_by.name || report.posted_by.email,
      },
      reported_by: {
        id: report.reported_by.id,
        name: report.reported_by.name || report.reported_by.email,
      },
      community_id: report.community_id,
      reportReason: report.reason,
      reported_at: report.reported_at,
      status: report.status,
    }));
  }, [data]);

  const renderActionsCell = useCallback(
    ({ row }: { row: { original: IReportedPostData; index?: number } }) => {
      return (
        <div className="relative">
          <button
            type="button"
            onClick={(e) => {
              const isOpening = dropdownOpen !== row.original.id;
              setDropdownOpen(isOpening ? row.original.id : null);
              setDropdownTriggerRect(
                isOpening ? e.currentTarget.getBoundingClientRect() : null
              );
            }}
            className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
            title="Actions"
            aria-label={`Actions for ${row.original.title}`}
            aria-haspopup="true"
            aria-expanded={dropdownOpen === row.original.id}
          >
            <KeenIcon
              type="filled"
              icon="dots-vertical"
              className="text-gray-500"
              aria-hidden="true"
            />
          </button>
          {dropdownOpen === row.original.id && (
            <div
              className="fixed inset-0 z-[9999]"
              onClick={() => {
                setDropdownOpen(null);
                setDropdownTriggerRect(null);
              }}
            >
              <div
                ref={dropdownMenuRef}
                className="fixed z-[10000] w-56 origin-top-right rounded-md bg-gray-100 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                style={
                  dropdownMenuPosition
                    ? {
                        top: dropdownMenuPosition.top,
                        left: dropdownMenuPosition.left,
                      }
                    : undefined
                }
                onClick={(e) => e.stopPropagation()}
                role="menu"
                aria-label="Post moderation actions"
              >
                <div className="py-1">
                  <button
                    type="button"
                    onClick={() => {
                      setReportDetailsState({
                        isOpen: true,
                        report: row.original,
                      });
                      setDropdownOpen(null);
                      setDropdownTriggerRect(null);
                    }}
                    className="flex w-full items-center justify-between px-4 py-2 text-sm text-blue-600 hover:bg-gray-100"
                    role="menuitem"
                    aria-label={`View report details for post ${row.original.title}`}
                    disabled={isLoading || isFetching}
                  >
                    <span>View details</span>
                    <KeenIcon
                      type="filled"
                      icon="eye"
                      className="text-blue-600"
                      aria-hidden="true"
                    />
                  </button>
                  {(hasPermission('post_management_update') ||
                    community?.user_role === CommunityRole.MODERATOR ||
                    community?.user_role === CommunityRole.COMMUNITY_ADMIN) && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setConfirmActionState({
                            isOpen: true,
                            postId: row.original.post_id,
                            action: PostAction.REJECT,
                          });
                          setDropdownOpen(null);
                          setDropdownTriggerRect(null);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-gray-100"
                        role="menuitem"
                        aria-label={`Reject report for post ${row.original.title}`}
                        disabled={isLoading || isFetching}
                      >
                        Reject Report
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setConfirmActionState({
                            isOpen: true,
                            postId: row.original.post_id,
                            action: PostAction.FLAG,
                          });
                          setDropdownOpen(null);
                          setDropdownTriggerRect(null);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-yellow-600 hover:bg-gray-100"
                        role="menuitem"
                        aria-label={`Flag post ${row.original.title}`}
                        disabled={isLoading || isFetching}
                      >
                        Flag
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setConfirmActionState({
                            isOpen: true,
                            postId: row.original.post_id,
                            action: PostAction.DELETE,
                          });
                          setDropdownOpen(null);
                          setDropdownTriggerRect(null);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                        role="menuitem"
                        aria-label={`Delete post ${row.original.title}`}
                        disabled={isLoading || isFetching}
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setConfirmActionState({
                            isOpen: true,
                            postId: row.original.post_id,
                            action: PostAction.HIDE,
                          });
                          setDropdownOpen(null);
                          setDropdownTriggerRect(null);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
                        role="menuitem"
                        aria-label={`Hide post ${row.original.title}`}
                        disabled={isLoading || isFetching}
                      >
                        Hide
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      );
    },
    [
      dropdownMenuPosition,
      dropdownOpen,
      isLoading,
      isFetching,
      hasPermission,
      community?.user_role,
    ]
  );
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessor: (row: IReportedPostData) => row.title,
        id: 'title',
        header: 'Title',
        enableSorting: false,
        cell: renderTitleCell(handleOpenPostModal),
        meta: { className: 'min-w-[150px] max-w-[200px]' },
      },
      {
        accessor: (row: IReportedPostData) => row.author.name,
        id: 'author',
        header: 'Posted By',
        enableSorting: false,
        cell: renderAuthorCell,
        meta: { className: 'min-w-[150px] max-w-[150px]' },
      },
      {
        accessor: (row: IReportedPostData) => row.reported_by.name,
        id: 'reported_by',
        header: 'Reported By',
        enableSorting: false,
        cell: renderReportedByCell,
        meta: { className: 'min-w-[150px] max-w-[150px]' },
      },
      {
        accessor: (row: IReportedPostData) => row.reported_at,
        id: 'reported_at',
        header: 'Reported At',
        enableSorting: false,
        cell: renderReportedAtCell,
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: IReportedPostData) => row.reportReason,
        id: 'reportReason',
        header: 'Reason',
        enableSorting: false,
        cell: renderReasonCell,
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: IReportedPostData) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: renderStatusCell,
        meta: { className: 'min-w-[200px]' },
      },
    ];

    if (
      hasPermission('post_management_update') ||
      community?.user_role === CommunityRole.MODERATOR ||
      community?.user_role === CommunityRole.COMMUNITY_ADMIN
    ) {
      baseColumns.push({
        accessor: () => '',
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: renderActionsCell,
        meta: { className: 'w-[80px]' },
      });
    }

    return baseColumns;
  }, [
    community?.user_role,
    dropdownMenuPosition,
    dropdownOpen,
    handleOpenPostModal,
    hasPermission,
    isFetching,
    isLoading,
    renderActionsCell,
  ]);

  return (
    <div className="card card-grid min-w-full overflow-x-auto">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex-shrink-0" role="search">
          <label
            className="input input-sm h-8 sm:h-10 flex items-center"
            htmlFor="search-reported-posts"
            aria-label="Search reported posts"
          >
            <KeenIcon
              type="filled"
              icon="magnifier"
              className="text-gray-500"
              aria-hidden="true"
            />
            <input
              id="search-reported-posts"
              placeholder="Search reported posts"
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
              aria-label="Search reported posts by title or content"
              role="searchbox"
              disabled={isLoading || isFetching}
            />
          </label>
        </div>
        {searchTerm && (
          <button
            type="button"
            className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10"
            onClick={() => {
              resetSearch();
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            aria-label="Clear search"
            title="Clear search"
            disabled={isLoading || isFetching}
          >
            <KeenIcon
              type="solid"
              icon="arrows-circle"
              className="text-gray-600"
              aria-hidden="true"
            />
            Clear
          </button>
        )}
      </div>
      <div className="card-body">
        {isLoading || isFetching ? (
          <div
            className="text-center py-4"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <KeenIcon
              type="filled"
              icon="spinner"
              className="text-gray-500 text-2xl animate-spin"
              aria-hidden="true"
            />
            <span className="ml-2 text-gray-700">Loading...</span>
          </div>
        ) : isError ? (
          <div
            className="text-center py-4 text-red-600"
            role="alert"
            aria-live="assertive"
          >
            Error loading reported posts. Please try again.
          </div>
        ) : (
          <>
            {!isLoading && !isError && filteredData.length === 0 && (
              <div className="text-center py-4">
                <p className="text-gray-500">No reported posts found</p>
              </div>
            )}
            {!isLoading && !isError && filteredData.length > 0 && (
              <DataGrid
                data-testid="reported-posts-data-grid"
                columns={columns}
                data={filteredData}
                rowSelect={false}
                loading={isLoading || isFetching}
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  totalItems: data?.total_items || totalItems,
                  totalPages: data?.total_pages || totalPages,
                  setPagination,
                  info: `{from} - {to} of {count}`,
                  sizes: [5, 10, 25, 50, 100],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                }}
                sorting={[{ id: 'title', desc: false }]}
                serverSide={true}
                hasActiveFilters={!!searchTerm}
                aria-label="Reported posts table"
              />
            )}
          </>
        )}
        <PostModal
          open={modalState.isOpen}
          onClose={() => {
            setModalState({ isOpen: false, post: null });
            setPostIdToFetch(null);
          }}
          post={modalState.post}
          communityId={communityId || ''}
        />
        <ReportDetailsModal
          open={reportDetailsState.isOpen}
          onClose={() => setReportDetailsState({ isOpen: false, report: null })}
          report={reportDetailsState.report}
        />
        <ConfirmationDialog
          open={confirmActionState.isOpen}
          onClose={() =>
            setConfirmActionState({
              isOpen: false,
              postId: null,
              action: null,
            })
          }
          onConfirm={handleModeratePost}
          message={`Are you sure you want to ${confirmActionState.action?.toLowerCase()} this post?`}
          cancelButtonText="No"
          confirmButtonText="Yes"
          showReasonCode={true}
          requireReason={true}
          reasonLabel="Reason for action"
          reasonPlaceholder="Select a reason"
          reasonCodes={[
            {
              value: PostModerationReasons.SPAM,
              label: intl.formatMessage({
                id: 'moderation.reason.spam',
              }),
            },
            {
              value: PostModerationReasons.OFF_TOPIC,
              label: intl.formatMessage({ id: 'moderation.reason.off_topic' }),
            },
            {
              value: PostModerationReasons.PERSONAL_DATA,
              label: intl.formatMessage({
                id: 'moderation.reason.personal_data',
              }),
            },
            {
              value: PostModerationReasons.SAFETY,
              label: intl.formatMessage({ id: 'moderation.reason.safety' }),
            },
            {
              value: PostModerationReasons.HATE_ABUSE,
              label: intl.formatMessage({ id: 'moderation.reason.hate_abuse' }),
            },
            {
              value: PostModerationReasons.ILLEGAL,
              label: intl.formatMessage({ id: 'moderation.reason.illegal' }),
            },
            {
              value: PostModerationReasons.SCAM_FRAUD,
              label: intl.formatMessage({ id: 'moderation.reason.scam_fraud' }),
            },
            {
              value: PostModerationReasons.NSFW,
              label: intl.formatMessage({ id: 'moderation.reason.nsfw' }),
            },
            {
              value: PostModerationReasons.DUPLICATE,
              label: intl.formatMessage({ id: 'moderation.reason.duplicate' }),
            },
            {
              value: PostModerationReasons.LOCATION_SENSITIVE,
              label: intl.formatMessage({
                id: 'moderation.reason.location_sensitive',
              }),
            },
            {
              value: PostModerationReasons.OTHER,
              label: intl.formatMessage({ id: 'moderation.reason.other' }),
              requiresText: true,
            },
          ]}
          confirmButtonVariant={
            confirmActionState.action === 'REJECT' ? 'destructive' : 'default'
          }
          aria-labelledby="action-confirmation-title"
          aria-describedby="action-confirmation-description"
        />
      </div>
    </div>
  );
};

export default ReportedPostListings;
